import { useAccount } from 'wagmi';
import { Box, Link, Tooltip, ColumnDef, Flex, SkeletonSquare, Image } from '@node-real/uikit';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import { ColoredInfoIcon } from '@node-real/icons';
import BN from 'bn.js';
import { useRouter } from 'next/router';
import {
  HistoryTable,
  formatNum,
  mobileMedia,
  useGetPrice,
  usePagination,
  useResponsive,
  useWithdrawHistoryPageKey,
} from '@op-bridge/bridge-core';

import { menus } from '../Menu';
import { useL1Withdraw, usePausedStatus, useWithdrawConfirm } from '../../hooks';
import {
  trimLongStr,
  divide10Exp,
  getDataFromCache,
  batchUpdate,
  deleteDataFromCache,
  formatLocalTime,
  getUTCZone,
} from '../../utils';
import { TxError } from '../modal/TxError';
import { FINALIZE_WAITING_PERIOD, NET_ENV, OPBNB_SCAN, PROVE_WAITING_PERIOD } from '../../env';
import { useGetTokenList } from '../../hooks/useGetTokenList';
import { WithdrawActionButton } from '../button/WithdrawActionButton';
import { HistoryConfirmWithdraw } from '../modal/HistoryConfirmWithdraw';
import { CountDownTimer } from '../ongoing/CountDownTimer';
import { Remaining } from '../ongoing/Remaining';
import { TableWrapper } from '../TableWrapper';

const FakeHistoryList = Array(9).fill({
  blockTimeStamp: 1682561592,
  l1TxHash: '...',
  l1ProveTxHash: '0x0000000000000000000000000000000000000000000000000000000000000000',
  gasPrice: 1500000008,
  gasUsed: 120187,
  hash: '0x0000000000000000000000000000000000000000000000000000000000000000',
  receiptsStatus: 0,
  value: '0x0',
});

const TooltipContent = () => {
  return (
    <Flex flexDirection={'column'} gap={4} gridGap={4}>
      <TooltipTitle>{`Waiting to Prove Period (Initiate → Prove Withdrawal)`}</TooltipTitle>
      <TooltipText>
        {`It usually takes ${
          NET_ENV === 'Testnet' ? '15 mins' : '1-2 hrs'
        }. After the output that contains your withdrawal has been proposed on BSC Testnet, you can manually Prove withdrawal.`}
      </TooltipText>
      <Box height={8}></Box>
      <TooltipTitle>{`Waiting to Finalize Period (Prove → Finalize Withdrawal)`}</TooltipTitle>
      <TooltipText>
        {NET_ENV === 'Testnet'
          ? `It usually takes 30 mins. After the withdrawal is proven, a window of waiting time begins in which to challenge any fraudulent activities.`
          : `It usually takes 7 days. After the withdrawal is proven, a window of waiting period begins in which to challenge any fraudulent activities.`}
      </TooltipText>
    </Flex>
  );
};

const TxStatus = [
  `Waiting to Prove`,
  'Ready to Prove',
  `Waiting to Finalize`,
  'Ready to Finalize',
  'Completed',
];
const cellheight = 56;
const pageSize = 20; // page size on web page
const loadDataSize = 500; // page size in api call

export const WithdrawHistory = () => {
  const { address: wagmiAddress, isDisconnected } = useAccount();
  const { page, handlePageChange } = usePagination();
  const { loadPausedStatus, pausedStatus, isPausedLoading } = usePausedStatus();
  // state
  const [address, setAddress] = useState<string | null>(null);
  const [initiateHash, setInitHash] = useState(''); // Bep20 token or BNB initiate hash
  const [proveHash, setProveHash] = useState(''); // prove hash
  const [buttonSubmit, setButtonSubmit] = useState<number | null>(null);
  const [finalizeHash, setFinalizeHash] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const [withdrawVal, setWithdrawVal] = useState(0);
  const [currentToken, setCurrentToken] = useState('BNB');
  const router = useRouter() as any;
  const { isMobile } = useResponsive();
  const { tokensList, isLoading } = useGetTokenList();
  const { data: bnbPrice } = useGetPrice('BNB');
  const { data: tokenPrice } = useGetPrice(currentToken);

  const {
    proveWithdraw,
    finalWithdraw,
    txProveFailedMsg,
    setTxProveFailedMsg,
    txFinalizeFailedMsg,
    setTxFinalizeFailedMsg,
    showError,
    handleErrorShow,
  } = useL1Withdraw();
  const { data: historyPage1, isFetching } = useWithdrawHistoryPageKey(
    address,
    '',
    loadDataSize,
    true,
    'p1',
  );

  const { data: historyPage2, isFetching: isFetching2 } = useWithdrawHistoryPageKey(
    address,
    historyPage1?.pageKey,
    loadDataSize,
    !!historyPage1?.pageKey,
    'p2',
  );

  const { data: historyPage3, isFetching: isFetching3 } = useWithdrawHistoryPageKey(
    address,
    historyPage2?.pageKey,
    loadDataSize,
    !!historyPage2?.pageKey,
    'p3',
  );

  const { data: historyPage4, isFetching: isFetching4 } = useWithdrawHistoryPageKey(
    address,
    historyPage3?.pageKey,
    loadDataSize,
    !!historyPage3?.pageKey,
    'p4',
  );

  const { showWithdrawalConfirm, handleWithdrawToggle, handleWithdrawConfirmShow } =
    useWithdrawConfirm();

  const [historyCache, setHistoryCache] = useState<{ [key: string]: string } | null>(null);
  const ref = useRef(true);

  const wholeHistoryList = useMemo(() => {
    if (
      historyPage2?.list.length > 0 &&
      historyPage3?.list.length > 0 &&
      historyPage4?.list.length > 0
    ) {
      return [
        ...historyPage1?.list,
        ...historyPage2?.list,
        ...historyPage3?.list,
        ...historyPage4?.list,
      ];
    } else if (historyPage2?.list.length > 0 && historyPage3?.list.length > 0) {
      return [...historyPage1?.list, ...historyPage2?.list, ...historyPage3?.list];
    } else if (historyPage2?.list.length > 0) {
      return [...historyPage1?.list, ...historyPage2?.list];
    } else if (historyPage1?.list.length > 0) {
      return [...historyPage1?.list];
    } else {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    historyPage1?.list,
    historyPage2?.list,
    historyPage3?.list,
    historyPage4?.list,
    wagmiAddress,
  ]);

  // move not completed and not auto withdrawal tx to the head of the list
  const sortedHistoryList = useMemo(() => {
    return wholeHistoryList?.sort((a, b) => {
      if (
        (a?.receiptsStatus !== 5 && b?.receiptsStatus === 5) ||
        (a?.receiptsStatus !== 5 &&
          b?.receiptsStatus !== 5 &&
          a?.blockTimeStamp >= b?.blockTimeStamp)
      ) {
        return -1;
      } else if (
        (a?.receiptsStatus === 5 && b?.receiptsStatus !== 5) ||
        (a?.receiptsStatus === 5 &&
          b?.receiptsStatus === 5 &&
          a?.blockTimeStamp < b?.blockTimeStamp)
      ) {
        return 1;
      } else {
        return 0;
      }
    });
  }, [wholeHistoryList]);

  const getHistoryCache = useCallback(async () => {
    if (typeof window !== 'undefined') {
      const cache = await getDataFromCache(window.location.hostname, {
        deleteCallback: () => setButtonSubmit(null),
      });
      if (cache) {
        // console.log(cache);
        return cache;
      }
    }
  }, []);

  useEffect(() => {
    setTimeout(() => (ref.current = false), 4000);
    if (typeof window !== 'undefined') {
      getHistoryCache().then((res) => {
        if (res) {
          setHistoryCache(res);
        } else {
          setHistoryCache(null);
        }
      });
    }
    return () => {
      ref.current = true;
    };
  }, [getHistoryCache, wagmiAddress]);

  // Refetch history page
  const refreshHistoryTable = useCallback(async () => {
    const cache = await getHistoryCache();
    batchUpdate(() => {
      if (cache) {
        // console.log('update cache', cache);
        setHistoryCache(cache);
      } else {
        setHistoryCache(null);
      }
    });
  }, [getHistoryCache]);

  /**
   * Get latest balance every 15s
   */
  useEffect(() => {
    let mount = true;
    if (!isDisconnected && wagmiAddress) {
      const requestAnimationFrameTimer = <RetT extends any>(
        fn: () => Promise<RetT>,
        time: number = 0,
      ) => {
        let now = Date.now;
        let startTime = now();
        let endTime = startTime;
        let uid;

        let timer = () => {
          if (mount) {
            uid = requestAnimationFrame(timer);
          }
          endTime = now();
          if (endTime - startTime >= time) {
            startTime = now();
            endTime = startTime;
            fn();
          }
        };
        uid = requestAnimationFrame(timer);
        return uid;
      };

      requestAnimationFrameTimer(async () => {
        if (pausedStatus && pausedStatus === true) {
          await loadPausedStatus();
        }
        refreshHistoryTable();
      }, 5000);
    }

    return () => {
      mount = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisconnected, wagmiAddress]);

  useEffect(() => {
    if (wagmiAddress) {
      setAddress(wagmiAddress);
      // back to page when user switch account
      handlePageChange(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wagmiAddress]);

  useEffect(() => {
    if (!wagmiAddress || isDisconnected) {
      if (window) {
        window.location.href = '/';
      }
    }
  }, [wagmiAddress, router, isDisconnected]);

  const timeColumn = useMemo(() => {
    return {
      accessorKey: 'time',
      header: 'Time',
      size: 210,
      cell: (info: any) => {
        const {
          row: { original: data },
        } = info;
        return (
          <DateTime>
            {formatLocalTime(data.blockTimeStamp * 1000, 'YYYY-M-DD HH:mm')} (UTC
            {getUTCZone(data.blockTimeStamp * 1000)})
          </DateTime>
        );
      },
    };
  }, []);

  const amountColumn = useMemo(() => {
    return {
      accessorKey: 'amount',
      header: 'Amount',
      size: 200,
      cell: (info: any) => {
        const {
          row: { original: data },
        } = info;
        let valStr = '';
        let tokenAsset = null;
        let icon = null;
        let assetName = data?.asset;
        try {
          tokenAsset = tokensList.filter((asset) => asset.symbol === assetName)[0];
          icon = menus.filter((asset) => asset.name === assetName)[0]?.icon;
          if (data?.asset === 'BNB') {
            if (data?.isAuto) {
              if (data.amount.includes('0x')) {
                const valBn = new BN(data.amount.slice(2), 16);
                valStr = Number(divide10Exp(valBn, 18)).toLocaleString('fullwide', {
                  maximumFractionDigits: 18,
                });
              } else {
                valStr = (Number(data.amount) * Math.pow(10, -18)).toLocaleString('fullwide', {
                  maximumFractionDigits: 18,
                });
              }
            } else {
              if (data.value.includes('0x')) {
                const valBn = new BN(data.value.slice(2), 16);
                valStr = Number(divide10Exp(valBn, 18)).toLocaleString('fullwide', {
                  maximumFractionDigits: 18,
                });
              } else {
                valStr = (Number(data.value) * Math.pow(10, -18)).toLocaleString('fullwide', {
                  maximumFractionDigits: 18,
                });
              }
            }
          } else {
            /**
             * Bep20 Token Amount
             */
            if (data?.amount !== null || data?.amount !== undefined) {
              const BNvalue = new BN(data?.amount.replace('0x', ''), 16);
              const amount = Number(divide10Exp(BNvalue, 18));
              valStr = Number(amount).toLocaleString('fullwide', {
                maximumFractionDigits: 18,
              });
            }
          }
        } catch (e) {
          //eslint-disable-next-line no-console
          console.log(e);
        }
        if (valStr?.length > 15) {
          return (
            <Tooltip placement="bottom-start" content={valStr}>
              <Flex
                flexDirection={'row'}
                alignItems={'center'}
                flexWrap={'nowrap'}
                whiteSpace={['break-spaces', 'nowrap']}
                fontSize={[14, 12]}
                fontWeight={500}
              >
                {tokenAsset && tokenAsset.logoURI ? (
                  <Image
                    width={20}
                    height={20}
                    mr={4}
                    alt={tokenAsset.symbol}
                    src={tokenAsset.logoURI}
                    borderRadius={'50%'}
                  />
                ) : (
                  <>
                    <Box mr={4}>{icon}</Box>
                  </>
                )}{' '}
                {`${trimLongStr(valStr, 15, 15, 0)} ${assetName}`}
              </Flex>
            </Tooltip>
          );
        } else {
          return (
            <Flex
              flexDirection={'row'}
              alignItems={'center'}
              flexWrap={'nowrap'}
              fontSize={[14, 12]}
              fontWeight={500}
            >
              {tokenAsset && tokenAsset.logoURI ? (
                <Image
                  width={20}
                  height={20}
                  mr={4}
                  alt={tokenAsset.symbol}
                  src={tokenAsset.logoURI}
                  borderRadius={'50%'}
                />
              ) : (
                <>{icon && <Box mr={4}>{icon}</Box>}</>
              )}{' '}
              {`${valStr} ${assetName}`}
            </Flex>
          );
        }
      },
    };
  }, [tokensList]);

  const txColumn = useMemo(() => {
    return {
      accessorKey: 'init_tx',
      header: 'Transaction',
      size: 220,
      cell: (info: any) => {
        const {
          row: { original: data },
        } = info;
        return (
          <Flex alignItems={'center'} mb={[4.5, 0]}>
            {data?.receiptsStatus !== 5 ? (
              <AutoWithdrawTag className={data?.isAuto ? 'isAuto' : ''}>
                {data?.isAuto ? 'Auto' : 'Manual'}
              </AutoWithdrawTag>
            ) : null}
            {isMobile && (
              <>
                <Box color={'readable.pageButton'}>Txn:&nbsp;</Box>
              </>
            )}
            <Link
              href={`${OPBNB_SCAN[NET_ENV]}/tx/${data?.hash}`}
              target="_blank"
              fontWeight={500}
              whiteSpace={'nowrap'}
              color={'readable.link'}
              _hover={{ color: 'scene.primary.normal' }}
            >
              {isMobile ? trimLongStr(data?.hash, 8, 4, 4) : trimLongStr(data?.hash)}
            </Link>
          </Flex>
        );
      },
    };
  }, [isMobile]);

  const withdrawTxColumns: ColumnDef<any> = useMemo(() => {
    return [
      isMobile ? amountColumn : timeColumn,
      isMobile ? txColumn : amountColumn,
      isMobile ? timeColumn : txColumn,
      {
        accessorKey: 'status',
        header: (
          <StatusHeader>
            Status
            <Tooltip
              padding={'8px 12px'}
              color={'readable.normal'}
              content={<TooltipContent />}
              width={400}
              maxWidth={400}
              placement="bottom-end"
              right={0}
            >
              <ColoredInfoIcon
                width={16}
                height={16}
                ml={4}
                cursor={'pointer'}
                _hover={{ color: 'scene.primary.active' }}
              />
            </Tooltip>
          </StatusHeader>
        ),
        size: 260,
        cell: (info: any) => {
          const {
            row: { original: data },
          } = info;
          let disabled = false;
          try {
            // delete cache if status updated
            if (
              historyCache &&
              data.hash in historyCache &&
              (data?.receiptsStatus === 2 || data?.receiptsStatus === 4)
            ) {
              const cacheStatus = historyCache[`${data?.hash as string}`];
              if (data.receiptsStatus <= cacheStatus) {
                disabled = true;
              } else if (data?.receiptsStatus > cacheStatus) {
                // get updated status from backend, we can remove cache
                if (typeof window !== 'undefined') {
                  deleteDataFromCache(window.location.hostname, data?.hash);
                }
              }
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
          }

          if (data.receiptsStatus < 5) {
            if (pausedStatus && pausedStatus === true) {
              return (
                <Tooltip
                  content={'Withdrawal function is temporarily suspended.'}
                  placement="bottom-end"
                >
                  <Status type="paused">Paused</Status>
                </Tooltip>
              );
            }
          }

          // Auto withdraw
          if (data?.statusChangeTimeStamp && data?.isAuto === true && data.receiptsStatus !== 5) {
            return (
              <Status className={'pure-text'} type="waiting">
                <Remaining
                  time={
                    data?.receiptsStatus === 3
                      ? (data?.statusChangeTimeStamp + FINALIZE_WAITING_PERIOD[NET_ENV]) * 1000
                      : (data?.statusChangeTimeStamp +
                          (NET_ENV === 'Testnet' ? PROVE_WAITING_PERIOD[NET_ENV] : 0) +
                          FINALIZE_WAITING_PERIOD[NET_ENV]) *
                        1000
                  }
                />
              </Status>
            );
          }
          // waiting to prove or finalize
          else if (
            (data.receiptsStatus === 1 || data.receiptsStatus === 3) &&
            data?.isAuto !== true
          ) {
            return (
              <Status className={'pure-text'} type="waiting">
                <Flex flexDirection={'row'} flexWrap={'nowrap'}>
                  {data?.statusChangeTimeStamp ? (
                    <CountDownTimer
                      time={
                        data.receiptsStatus === 1
                          ? data?.statusChangeTimeStamp + PROVE_WAITING_PERIOD[NET_ENV]
                          : data?.statusChangeTimeStamp + FINALIZE_WAITING_PERIOD[NET_ENV]
                      }
                      status={data.receiptsStatus}
                      prefix={TxStatus[data.receiptsStatus - 1]}
                    />
                  ) : (
                    TxStatus[data.receiptsStatus - 1]
                  )}
                </Flex>
              </Status>
            );
          } else if (data.receiptsStatus === 2 && data?.isAuto !== true) {
            return disabled ? (
              <Status type="waiting">Proving Withdrawal</Status>
            ) : (
              <Status type="waiting">{TxStatus[data.receiptsStatus - 1]}</Status>
            );
          } else if (data.receiptsStatus === 4 && data?.isAuto !== true) {
            return disabled ? (
              <Status type="waiting">Finalizing Withdrawal</Status>
            ) : (
              <Status type="waiting">{TxStatus[data.receiptsStatus - 1]}</Status>
            );
          } else if (data.receiptsStatus === 5) {
            return <Status type="complete">{TxStatus[data.receiptsStatus - 1]}</Status>;
          } else {
            return '--';
          }
        },
      },
      {
        accessorKey: 'action',
        header: <StatusHeader>Action</StatusHeader>,
        size: 100,
        cell: (info: any) => {
          const {
            row: { original: data },
          } = info;

          let disabled = false;
          try {
            // delete cache if status updated
            if (
              historyCache &&
              data.hash in historyCache &&
              (data?.receiptsStatus === 2 || data?.receiptsStatus === 4)
            ) {
              const cacheStatus = historyCache[`${data?.hash as string}`];
              if (data.receiptsStatus <= cacheStatus) {
                disabled = true;
              }
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
          }

          if (data.receiptsStatus < 5) {
            if (pausedStatus && pausedStatus === true) {
              return (
                <Tooltip
                  content={'Withdrawal function is temporarily suspended.'}
                  placement="bottom-end"
                >
                  <Status type="paused">Paused</Status>
                </Tooltip>
              );
            }
          }

          // waiting for prove or waiting for finalize
          return (
            <WithdrawActionButton
              data={data}
              proveWithdraw={proveWithdraw}
              finalWithdraw={finalWithdraw}
              disabled={disabled}
              refreshHistoryTable={refreshHistoryTable}
              setButtonSubmit={setButtonSubmit}
              setWithdrawVal={setWithdrawVal}
              setProveHash={setProveHash}
              setFinalizeHash={setFinalizeHash}
              setInitHash={setInitHash}
              setCurrentToken={setCurrentToken}
              setIsConfirming={setIsConfirming}
              handleWithdrawToggle={handleWithdrawToggle}
            />
          );
        },
      },
    ];
  }, [
    proveWithdraw,
    finalWithdraw,
    pausedStatus,
    isMobile,
    historyCache,
    txColumn,
    timeColumn,
    amountColumn,
    refreshHistoryTable,
    handleWithdrawToggle,
  ]);

  const isLoadingColumns = useMemo(
    () =>
      withdrawTxColumns.map((column) => ({
        ...column,
        cell: () => <SkeletonSquare style={{ width: '80%' }} />,
      })),
    [withdrawTxColumns],
  );
  const finalColumns =
    (isFetching || isFetching2 || isFetching3 || isFetching4 || isPausedLoading || isLoading) &&
    ref.current &&
    !isMobile
      ? isLoadingColumns
      : withdrawTxColumns;

  if (isDisconnected) {
    return <div></div>;
  }

  return (
    <Container>
      <Box>
        <Box>
          <HistoryTable
            type="withdraw"
            data={
              (isFetching ||
                isFetching2 ||
                isFetching3 ||
                isFetching4 ||
                isPausedLoading ||
                isLoading) &&
              ref.current
                ? isMobile
                  ? sortedHistoryList?.slice((page - 1) * pageSize, page * pageSize - 1)
                  : FakeHistoryList
                : sortedHistoryList?.slice((page - 1) * pageSize, page * pageSize - 1) || []
            }
            withdrawTxColumns={finalColumns}
            isLoading={
              isFetching ||
              isFetching2 ||
              isFetching3 ||
              isFetching4 ||
              isPausedLoading ||
              isLoading
            }
            pageSize={20}
            history={{
              list: sortedHistoryList?.slice((page - 1) * pageSize, page * pageSize - 1) || [],
              total: historyPage1?.total_num || 0,
            }}
            cellHeight={cellheight}
            emptyText={'No Withdrawal History.'}
            topContent={
              <TableWrapper
                page={page}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                text={
                  historyPage1?.total_num > 2000
                    ? `latest 2000 withdrawal transactions (Total of ${formatNum(
                        historyPage1?.total_num || 0,
                      )})`
                    : `Total of ${formatNum(historyPage1?.total_num || 0)} withdrawal transactions`
                }
                total={(historyPage1?.total_num > 2000 ? 2000 : historyPage1?.total_num) || 0}
              />
            }
            bottomContent={
              <TableWrapper
                page={page}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                text={`Total of ${formatNum(historyPage1?.total_num || 0)} withdrawal transactions`}
                total={(historyPage1?.total_num > 2000 ? 2000 : historyPage1?.total_num) || 0}
              />
            }
          />
        </Box>
      </Box>

      {showWithdrawalConfirm && (
        <HistoryConfirmWithdraw
          withdrawVal={withdrawVal}
          tokenName={currentToken}
          isOpen={showWithdrawalConfirm}
          handleOpen={(open: boolean) => {
            handleWithdrawConfirmShow(open);
            setButtonSubmit(null);
          }}
          buttonSubmit={buttonSubmit}
          setButtonSubmit={setButtonSubmit}
          bnbPrice={bnbPrice}
          tokenPrice={tokenPrice}
          initiateWithdrawHash={initiateHash}
          proveWithdrawHash={proveHash}
          finalizeWithdrawHash={finalizeHash}
          isInitConfirming={isConfirming}
          historyCache={historyCache}
          historyList={wholeHistoryList || []}
        />
      )}

      {txProveFailedMsg && (
        <TxError
          title={`Prove Withdrawal Failed`}
          isOpen={showError}
          handleOpen={() => {
            setTxProveFailedMsg('');
            handleErrorShow(false);
          }}
          errorCode={txProveFailedMsg}
        />
      )}

      {txFinalizeFailedMsg && (
        <TxError
          title={`Finalize Withdrawal Failed`}
          isOpen={showError}
          handleOpen={() => {
            setTxFinalizeFailedMsg('');
            handleErrorShow(false);
          }}
          errorCode={txFinalizeFailedMsg}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  max-width: 1181px;
  width: 100%;
  padding: 16px 24px 0;
  margin: 0 auto;
  ${mobileMedia} {
    padding: 0 12px;
  }
`;

const StatusHeader = styled(Flex)`
  cursor: pointer;
  align-items: center;
  ${mobileMedia} {
    display: none;
  }
`;

const TooltipTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
`;

const TooltipText = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${(props: any) => props.theme.colors.readable?.grey10};
`;

const DateTime = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  white-space: nowrap;
  ${mobileMedia} {
    font-size: 12px;
  }
`;

const Status = styled.div<{ type: string }>`
  padding: 4.5px 8px;
  font-weight: 600;
  font-size: 12px;
  line-height: 15px;
  border-radius: 24px;
  cursor: ${(props: any) => (props.type === 'paused' ? 'pointer' : 'auto')};
  background: ${(props: any) =>
    props.type === 'waiting'
      ? props.theme.colors.scene.warning.opacity
      : props.type === 'paused'
      ? props.theme.colors.scene?.danger?.opacity
      : props.theme.colors.bg?.status};
  color: ${(props: any) =>
    props.type === 'waiting'
      ? props.theme.colors.scene.warning.normal
      : props.type === 'paused'
      ? props.theme.colors.scene?.danger?.active
      : props.theme.colors.readable?.top.secondary};
  width: 100%;
  width: fit-content;
  white-space: nowrap;
  ${mobileMedia} {
    padding: 3px 8px;
    max-width: 175px;
    &.pure-text {
      white-space: break-spaces;
      text-align: center;
    }
  }
`;

const AutoWithdrawTag = styled(Box)`
  font-size: 12px;
  margin-right: 8px;
  padding: 3px 8px;
  height: 22px;
  font-weight: 700;
  font-family: Space Grotesk;
  border-radius: 12px;
  color: ${(props: any) => props.theme.colors.scene?.orange?.normal};
  background: rgba(241, 89, 3, 0.15);

  &.isAuto {
    color: #02a364;
    background: rgba(2, 192, 118, 0.1);
  }
  ${mobileMedia} {
    margin-right: 2px;
    height: 16px;
    padding: 0 8px;
  }
`;
